import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { AccountModal } from './AccountModal';
import type { User } from './types';

// Open the edit-user modal from ANYWHERE a user's avatar appears — not just the
// Account page. The host lives once at the top of the admin shell; any component
// (a feed row, a heatmap row head, the People table) calls openUserEdit(email)
// or openUserCreate() and the same master-detail modal opens over the current
// page. There is exactly one modal in the app, so "edit access" looks and
// behaves identically wherever it's launched.
interface AccountModalApi {
  openUserEdit: (email: string) => void;
  openUserCreate: () => void;
}

const Ctx = createContext<AccountModalApi | null>(null);

// Read the opener. Returns null outside the provider (e.g. the staff-only shell),
// so a caller can render a plain avatar when there's no modal to open.
export function useAccountModal(): AccountModalApi | null {
  return useContext(Ctx);
}

type Open = { mode: 'edit'; email: string } | { mode: 'create' } | null;

export function AccountModalProvider({ myEmail, children }: { myEmail: string; children: ReactNode }) {
  const [open, setOpen] = useState<Open>(null);
  const openUserEdit = useCallback((email: string) => setOpen({ mode: 'edit', email }), []);
  const openUserCreate = useCallback(() => setOpen({ mode: 'create' }), []);
  const close = useCallback(() => setOpen(null), []);

  return (
    <Ctx.Provider value={{ openUserEdit, openUserCreate }}>
      {children}
      {/* The allowlist fetch is LAZY — mounted only while the modal is open — so a
          page that never opens it pays nothing. The 'account:users' key is shared
          with the Account page, so on that page the cache is already warm and the
          modal opens instantly. */}
      {open && <AccountModalHost open={open} myEmail={myEmail} onClose={close} />}
    </Ctx.Provider>
  );
}

function AccountModalHost({ open, myEmail, onClose }: { open: NonNullable<Open>; myEmail: string; onClose: () => void }) {
  const usersRes = useResource('account:users', () => api<User[]>('/account/users').then((r) => r.data), {
    invalidatedBy: ['account'],
  });
  const users = usersRes.state.data;
  // Until the allowlist lands the modal can't build its rail or find the target;
  // it opens the moment data is there (instant when the cache is warm).
  if (!users) return null;
  return (
    <AccountModal
      users={users}
      selectedEmail={open.mode === 'edit' ? open.email : null}
      myEmail={myEmail}
      onClose={onClose}
      onChanged={usersRes.reload}
    />
  );
}
